import {
  CalendarIcon,
  InfoIcon,
  PersonIcon,
  ShareAndroidIcon
} from "@primer/octicons-react";
import {
  Box,
  Label,
  Text
} from "@primer/react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Table } from "@primer/react/lib-esm/DataTable";
import styled from "styled-components";
import { hocSinhApi } from "../../../api/hocSinhApi";
import DataTable from "../../../components/ui/data-table";
import UserAvatar from "../../../components/user-avatar";
import { IHocSinhSelectRequest } from "../../../models/request/hocsinh/IHocSinhSelectRequest";
import { IHocSinhTongHopAceItemResponse } from "../../../models/response/hocsinh/IHocSinhTongHopAceItemResponse";
import { RootState } from "../../../state/reducers";
interface IHocSinhGroup {
  key: string;
  students: IHocSinhTongHopAceItemResponse[];
}
type AnhChiEmProps = {
  ts_hocsinh_id: number,
}
const StyledContainer = styled(Box)`
  padding: 12px;
`;

const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const StudentCard = styled.div`
  flex: 1;
  min-width: 300px;
  padding: 20px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0366d6;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
`;
const AnhChiEm = (props: AnhChiEmProps) => {
  const { nam_hoc, dm_coso_id } = useSelector((x: RootState) => x.common);
  const dispatch = useDispatch();
  const { dm_truong_selected_id } = useSelector((x: RootState) => x.common);
  const [dm_trangthaihocsinh_ids, setDmTrangThaiIds] = useState<number[]>([]);
  const [allHocSinhs, setAllHocSinhs] = useState<any[]>([]);
  const loadAllHocSinh = useCallback(async () => {
    const payload: IHocSinhSelectRequest = {
      nam_hoc,
      searchText: "",
      dm_trangthaihocsinh_ids: dm_trangthaihocsinh_ids,
      dm_truong_id: dm_truong_selected_id || 0,
      dm_coso_id: dm_coso_id,
      dm_khoi_id: 0,
      dm_he_id: 0,
      dm_lop_id: 0,
    };
    const res = await hocSinhApi.SelectTongHopAce(payload);
    if (res.is_success) {
      setAllHocSinhs(res.data);
    }
  }, [nam_hoc, dm_truong_selected_id, dm_coso_id, dm_trangthaihocsinh_ids]);
  useEffect(() => {
    loadAllHocSinh();
  }, []);
  const groupedData = useMemo(() => {
    const groups: IHocSinhGroup[] = [];
    const processedIds = new Set<number>();

    allHocSinhs.forEach(student => {
      if (processedIds.has(student.id)) return;
      if (!student.ace_ids) {
        groups.push({
          key: `single_${student.id}`,
          students: [student]
        });
        processedIds.add(student.id);
      } else {
        const siblingIds = student.ace_ids.split(';').map((id: any) => parseInt(id.trim()));
        const siblings = allHocSinhs.filter(s =>
          siblingIds.includes(s.id) || s.id === student.id
        );
        groups.push({
          key: student.ace_ids,
          students: siblings.sort((a, b) => a.ho_ten.localeCompare(b.ho_ten))
        });
        siblings.forEach(s => processedIds.add(s.id));
      }
    });

    // Sắp xếp nhóm
    const sortedGroups = groups.sort((a, b) => {
      if (a.students.length > 1 && b.students.length === 1) return -1;
      if (a.students.length === 1 && b.students.length > 1) return 1;
      return 0;
    });

    const filteredGroups = sortedGroups.filter(group =>
      group.students.some(student => student.id === props.ts_hocsinh_id)
    );

    return filteredGroups;
  }, [allHocSinhs]);

  return (<>
    {groupedData.length === 0 ? (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh", // Giữ nội dung căn giữa nhưng có thể mở rộng nếu cần
          textAlign: "center"
        }}
      >
        <img
          src="https://true-north-school.s3.cloudfly.vn/fee/datagrid_empty"
          alt="Không có dữ liệu"
          style={{ width: "60px", height: "60px", marginBottom: "5px" }}
        />
        <Box sx={{ fontSize: "13px", fontWeight: "400", color: "#8C8C8C" }}>
          Chưa có dữ liệu anh chị em
        </Box>
      </Box>


    ) : (
      <StyledContainer>
        <StyledTable>
          <Box sx={{
            height: 'calc(100vh - 350px)',
            overflowY: 'auto',
          }}>
            <Table.Body>
              {groupedData.map((group, groupIndex) => (
                <Table.Row key={group.key}>
                  <Table.Cell style={{ padding: '5px' }} colSpan={6}>
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 3,
                      padding: '12px'
                    }}>
                      {group.students.map((student) => (
                        <StudentCard
                          key={student.id}
                        >
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 0
                          }}>
                            <UserAvatar fullName={student.ho_ten} size="large" />
                            <Box sx={{ flex: 1 }}>
                              <Text sx={{ fontWeight: 600, color: '#0969da', fontSize: 2 }}>
                                {student.ho_ten}
                              </Text>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Text sx={{ fontSize: '12px', color: '#57606a' }}>
                                  {student.ma_hs}
                                </Text>
                                <Label variant={
                                  student.trang_thai === "New Enrollment" ? "accent" :
                                    student.trang_thai === "Re-Enrollment" ? "success" :
                                      student.trang_thai === "Tiềm năng" ? "sponsors" :
                                        student.trang_thai === "Thôi học" ? "danger" : "default"
                                }
                                  sx={{ fontSize: '11px' }}
                                >
                                  {student.trang_thai}
                                </Label>
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 2,
                            background: '#f6f8fa',
                            p: 2,
                            mt: 0,
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ color: '#0969da' }}><CalendarIcon size={14} /></Box>
                              <Text>{moment(student.ngay_sinh).format('DD/MM/YYYY')}</Text>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ color: '#0969da' }}><InfoIcon size={14} /></Box>
                              <Text>{student.gioi_tinh}</Text>
                            </Box>
                          </Box>
                          <Box sx={{ mt: 2, fontSize: '13px' }}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1
                            }}>
                              <Box sx={{ color: '#FF9D3D' }}><ShareAndroidIcon size={14} /></Box>
                              <Text sx={{ color: '#57606a', mt: 0 }}>
                                {student.ten_truong} • {student.ten_khoi} • {student.ten_lop}
                              </Text>
                            </Box>
                            <Label
                              variant={
                                student.ten_he === "Bilingual Track" ? "accent" :
                                  student.ten_he === "International Track" ? "success" : "default"
                              }
                              sx={{ fontSize: '12px' }}
                            >
                              {student.ten_he}
                            </Label>
                          </Box>
                        </StudentCard>
                      ))}
                    </Box>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>  </Box>

        </StyledTable>
      </StyledContainer>
    )}
  </>
  );
};

export default AnhChiEm;